import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import {
  i18n,
  assertValidTiers,
  InvalidPricingTierError,
  pricingTierMatchesBillingMode,
} from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { formatCents } from '../lib/format.js';

const { t } = i18n;

type PricingKind = 'PER_DAY' | 'FIXED_MONTHLY';

interface TierBand {
  fromDay: number;
  toDay: number | null;
  rateCents: number;
}

interface PricingTier {
  id: string;
  name: string;
  scope: 'GLOBAL' | 'CUSTOMER';
  kind: PricingKind;
  isDefault: boolean;
  tiers: TierBand[];
  monthlyCents: number | null;
}

interface CustomerRow {
  id: string;
  code: string;
  name: string;
  billingMode: 'PER_PALLET_DAY' | 'FIXED_MONTHLY';
  pricingTierId: string | null;
}

// One editable row in the per-day form. `fromDay` is derived (band 1 starts at
// day 1, each later band the day after the previous ends); the user only
// enters the band's end day and rate.
interface BandRow {
  toDay: string; // empty = open-ended (allowed on the last row only)
  rate: string; // euros, comma or dot decimal
}

function eurosToCents(v: string): number {
  return Math.round(Number(v.replace(',', '.')) * 100);
}

export function PricingTiersPage() {
  const [rows, setRows] = useState<PricingTier[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const [name, setName] = useState('');
  const [scope, setScope] = useState<'GLOBAL' | 'CUSTOMER'>('GLOBAL');
  const [kind, setKind] = useState<PricingKind>('PER_DAY');
  const [isDefault, setIsDefault] = useState(false);
  const [bands, setBands] = useState<BandRow[]>([{ toDay: '', rate: '' }]);
  const [monthly, setMonthly] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRows(null);
    api<PricingTier[]>('/pricing-tiers')
      .then(setRows)
      .catch((err: unknown) => setListError(String(err)));
  }, [reload]);

  function buildTiers(): TierBand[] {
    let from = 1;
    return bands.map((b, i) => {
      const isLast = i === bands.length - 1;
      const toDay =
        b.toDay.trim() === '' ? (isLast ? null : Number.NaN) : Math.trunc(Number(b.toDay));
      const rateCents = eurosToCents(b.rate);
      const band: TierBand = { fromDay: from, toDay, rateCents };
      from = (toDay ?? 0) + 1;
      return band;
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);

    let body: Record<string, unknown>;
    if (kind === 'PER_DAY') {
      const tiers = buildTiers();
      if (
        tiers.some((b) => (b.toDay !== null && Number.isNaN(b.toDay)) || !Number.isFinite(b.rateCents))
      ) {
        setFormError(t('pricing.invalid_tiers'));
        return;
      }
      try {
        assertValidTiers(tiers);
      } catch (err) {
        setFormError(
          err instanceof InvalidPricingTierError
            ? `${t('pricing.invalid_tiers')} (${err.message})`
            : t('pricing.invalid_tiers'),
        );
        return;
      }
      body = { name: name.trim(), scope, kind, isDefault, tiers };
    } else {
      const monthlyCents = eurosToCents(monthly);
      if (!Number.isFinite(monthlyCents) || monthlyCents <= 0) {
        setFormError(t('pricing.invalid_monthly'));
        return;
      }
      body = { name: name.trim(), scope, kind, isDefault, monthlyCents };
    }

    setBusy(true);
    try {
      await api('/pricing-tiers', { method: 'POST', body });
      setName('');
      setScope('GLOBAL');
      setKind('PER_DAY');
      setIsDefault(false);
      setBands([{ toDay: '', rate: '' }]);
      setMonthly('');
      setReload((k) => k + 1);
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.code === 'invalid_tiers' || err.code === 'invalid_input')
      ) {
        setFormError(t('pricing.invalid_tiers'));
      } else {
        setFormError(t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  function updateBand(index: number, patch: Partial<BandRow>) {
    setBands((prev) => prev.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  }
  function addBand() {
    setBands((prev) => [...prev, { toDay: '', rate: '' }]);
  }
  function removeBand(index: number) {
    setBands((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  }

  // Derived "from day" per editable row, mirroring buildTiers().
  let derivedFrom = 1;
  const fromByRow = bands.map((b) => {
    const start = derivedFrom;
    derivedFrom = (b.toDay.trim() === '' ? start : Math.trunc(Number(b.toDay))) + 1;
    return start;
  });

  return (
    <section className="space-y-8 max-w-3xl">
      <header>
        <h2 className="text-xl font-semibold">{t('pricing.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('pricing.subtitle')}</p>
      </header>

      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4 rounded-md border border-border p-4 text-sm">
        <h3 className="font-medium">{t('pricing.new.title')}</h3>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('pricing.field.name')}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={120}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </Field>
          <Field label={t('pricing.field.kind')}>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as PricingKind)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            >
              <option value="PER_DAY">{t('pricing.kind.PER_DAY')}</option>
              <option value="FIXED_MONTHLY">{t('pricing.kind.FIXED_MONTHLY')}</option>
            </select>
          </Field>
          <Field label={t('pricing.field.scope')}>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as typeof scope)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            >
              <option value="GLOBAL">{t('pricing.scope.GLOBAL')}</option>
              <option value="CUSTOMER">{t('pricing.scope.CUSTOMER')}</option>
            </select>
          </Field>
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          <span>{t('pricing.field.default')}</span>
        </label>

        {kind === 'PER_DAY' ? (
          <div className="space-y-2">
            <span className="font-medium">{t('pricing.bands.title')}</span>
            <div className="space-y-2">
              {bands.map((b, i) => {
                const isLast = i === bands.length - 1;
                return (
                  <div key={i} className="flex flex-wrap items-end gap-2">
                    <div className="text-xs text-muted-foreground pb-2">
                      {t('pricing.band.from')}{' '}
                      <span className="tabular-nums">{fromByRow[i]}</span>
                    </div>
                    <label className="space-y-1">
                      <span className="block text-xs text-muted-foreground">
                        {t('pricing.band.upto')}
                      </span>
                      <input
                        type="number"
                        min={fromByRow[i]}
                        step={1}
                        value={b.toDay}
                        onChange={(e) => updateBand(i, { toDay: e.target.value })}
                        placeholder={isLast ? t('pricing.band.open_ended') : ''}
                        className="w-28 rounded border border-border bg-background px-2 py-1.5 tabular-nums"
                      />
                    </label>
                    <label className="space-y-1">
                      <span className="block text-xs text-muted-foreground">
                        {t('pricing.band.rate')}
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={b.rate}
                        onChange={(e) => updateBand(i, { rate: e.target.value })}
                        required
                        placeholder="0,00"
                        className="w-28 rounded border border-border bg-background px-2 py-1.5 tabular-nums"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => removeBand(i)}
                      disabled={bands.length <= 1}
                      aria-label={t('common.delete')}
                      className="rounded border border-border px-2 py-1.5 text-xs disabled:opacity-40"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={addBand}
              className="rounded border border-border px-2 py-1 text-xs"
            >
              + {t('pricing.bands.add')}
            </button>
            <p className="text-xs text-muted-foreground">{t('pricing.bands.hint')}</p>
          </div>
        ) : (
          <Field label={t('pricing.field.monthly')}>
            <input
              type="text"
              inputMode="decimal"
              value={monthly}
              onChange={(e) => setMonthly(e.target.value)}
              required
              placeholder="0,00"
              className="w-40 rounded border border-border bg-background px-2 py-1.5 tabular-nums"
            />
          </Field>
        )}

        {formError ? <div className="text-red-600">{formError}</div> : null}

        <button
          type="submit"
          disabled={busy || !name.trim()}
          className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
        >
          {busy ? t('common.loading') : t('pricing.create')}
        </button>
      </form>

      <div className="space-y-2">
        <h3 className="font-medium">{t('pricing.list.title')}</h3>
        {listError ? (
          <div className="text-sm text-red-600">{listError}</div>
        ) : rows === null ? (
          <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div className="rounded border border-dashed border-border p-4 text-sm text-muted-foreground">
            {t('pricing.list.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('pricing.col.name')}</th>
                  <th className="px-3 py-2 font-medium">{t('pricing.col.kind')}</th>
                  <th className="px-3 py-2 font-medium">{t('pricing.col.scope')}</th>
                  <th className="px-3 py-2 font-medium">{t('pricing.col.detail')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((tier) => (
                  <tr key={tier.id} className="border-t border-border align-top">
                    <td className="px-3 py-2">
                      {tier.name}
                      {tier.isDefault ? (
                        <span className="ml-2 text-[10px] uppercase tracking-wide rounded bg-muted px-1 py-0.5">
                          {t('customer.pricing_tier.default')}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs">{t(`pricing.kind.${tier.kind}`)}</td>
                    <td className="px-3 py-2 text-xs">{t(`pricing.scope.${tier.scope}`)}</td>
                    <td className="px-3 py-2 text-xs">{tierDetail(tier)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AssignSection tiers={rows ?? []} />
    </section>
  );
}

function tierDetail(tier: PricingTier): string {
  if (tier.kind === 'FIXED_MONTHLY') {
    return tier.monthlyCents === null
      ? '—'
      : `${formatCents(tier.monthlyCents)}/${t('pricing.per_month')}`;
  }
  return tier.tiers
    .map((b) => {
      const range = b.toDay === null ? `${b.fromDay}+` : `${b.fromDay}–${b.toDay}`;
      return `${range}: ${formatCents(b.rateCents)}/${t('pricing.per_day')}`;
    })
    .join(' · ');
}

function AssignSection({ tiers }: { tiers: PricingTier[] }) {
  const [customers, setCustomers] = useState<CustomerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setCustomers(null);
    api<CustomerRow[]>('/customers')
      .then(setCustomers)
      .catch((err: unknown) => setError(String(err)));
  }, [reload]);

  async function assign(customerId: string, tierId: string) {
    setSavingId(customerId);
    setError(null);
    try {
      await api(`/pricing-tiers/customer/${customerId}`, {
        method: 'PUT',
        body: { tierId: tierId || null },
      });
      setReload((k) => k + 1);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'pricing_kind_mismatch'
          ? t('pricing.kind_mismatch')
          : t('common.error'),
      );
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="space-y-2">
      <h3 className="font-medium">{t('pricing.assign.title')}</h3>
      <p className="text-sm text-muted-foreground">{t('pricing.assign.subtitle')}</p>
      {error ? <div className="text-sm text-red-600">{error}</div> : null}
      {customers === null ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : customers.length === 0 ? (
        <div className="rounded border border-dashed border-border p-4 text-sm text-muted-foreground">
          {t('pricing.assign.empty')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-3 py-2 font-medium">{t('pricing.assign.col.customer')}</th>
                <th className="px-3 py-2 font-medium">{t('customer.col.billing_mode')}</th>
                <th className="px-3 py-2 font-medium">{t('customer.col.pricing_tier')}</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="px-3 py-2">
                    {c.name} <span className="font-mono text-xs text-muted-foreground">{c.code}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">{t(`customer.billing.${c.billingMode}`)}</td>
                  <td className="px-3 py-2">
                    <select
                      value={c.pricingTierId ?? ''}
                      disabled={savingId === c.id}
                      onChange={(e) => void assign(c.id, e.target.value)}
                      className="w-full rounded border border-border bg-background px-2 py-1 disabled:opacity-50"
                    >
                      <option value="">{t('customer.pricing_tier.none')}</option>
                      {tiers
                        // Only offer plans/tariffs whose kind fits the customer's
                        // billing mode; keep any (legacy) current pick visible.
                        .filter(
                          (tier) =>
                            pricingTierMatchesBillingMode(tier.kind, c.billingMode) ||
                            tier.id === c.pricingTierId,
                        )
                        .map((tier) => (
                          <option key={tier.id} value={tier.id}>
                            {tier.name} — {t(`pricing.kind.${tier.kind}`)}
                            {tier.isDefault ? ` (${t('customer.pricing_tier.default')})` : ''}
                          </option>
                        ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}
